// File: frontend/src/pages/fees/FeeDashboard.jsx

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSchool } from '../../context/SchoolContext';
import api from '../../utils/axios';
import FeeReceipt from './FeeReceipt';

const FeeDashboard = () => {
  const { school } = useSchool();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  /**
   * Fetch fee dashboard financial metrics and recent transactions
   */
  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/api/v1/fees/dashboard');
      if (response.data?.success) {
        setStats(response.data.data);
      } else {
        setError(response.data?.message || 'Failed to load fee dashboard metrics');
      }
    } catch (err) {
      console.error('Error loading fee dashboard stats:', err);
      setError(err.response?.data?.message || err.message || 'Error communicating with server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Header Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard/admin" className="w-10 h-10 rounded-xl overflow-hidden bg-white border border-slate-200 p-1 flex items-center justify-center shadow-sm">
              {school?.logo ? (
                <img src={school.logo} alt="Logo" className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="font-extrabold text-primary text-sm">SMS</span>
              )}
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">Fee Control Center</h1>
              <p className="text-xs text-slate-500">School financial management & accounts overview</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/fees/collect"
              style={{ backgroundColor: school?.button_color || 'var(--button-color)' }}
              className="text-white font-medium px-4 py-2 rounded-xl text-sm transition shadow-sm hover:brightness-110 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Collect Fee
            </Link>
            <Link
              to="/fees/structure"
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2 rounded-xl text-sm transition"
            >
              Fee Structure
            </Link>
            <Link
              to="/fees/defaulters"
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2 rounded-xl text-sm transition"
            >
              Defaulters List
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm flex items-center justify-between shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-800">✕</button>
          </div>
        )}

        {/* 4 Financial Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          
          {/* Card 1: Collected This Month */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">Current Month</span>
            </div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Collected This Month</p>
            <h3 className="text-3xl font-extrabold text-emerald-600 mt-1">
              {loading ? '...' : `Rs. ${(stats?.collected_this_month || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}`}
            </h3>
          </div>

          {/* Card 2: Pending This Month */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-red-50 text-red-600 flex items-center justify-center font-bold">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-xs font-bold text-red-700 bg-red-50 px-2.5 py-1 rounded-full">Outstanding</span>
            </div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pending This Month</p>
            <h3 className="text-3xl font-extrabold text-red-600 mt-1">
              {loading ? '...' : `Rs. ${(stats?.pending_this_month || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}`}
            </h3>
          </div>

          {/* Card 3: Total Defaulters */}
          <div 
            onClick={() => navigate('/fees/defaulters')}
            className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md cursor-pointer transition"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <span className="text-xs font-bold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">Students</span>
            </div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Defaulters</p>
            <h3 className="text-3xl font-extrabold text-amber-600 mt-1">
              {loading ? '...' : stats?.total_defaulters || 0}
            </h3>
          </div>

          {/* Card 4: Quick Action */}
          <div className="bg-gradient-to-br from-primary to-blue-700 rounded-2xl p-6 text-white shadow-md flex flex-col justify-between">
            <div>
              <span className="text-xs font-bold text-blue-100 bg-white/20 px-2.5 py-1 rounded-full">Quick Task</span>
              <h4 className="text-lg font-bold mt-2">New Payment</h4>
              <p className="text-xs text-blue-100 mt-1">Collect fee & issue receipt</p>
            </div>
            <Link
              to="/fees/collect"
              className="mt-4 inline-flex items-center justify-center px-4 py-2 bg-white text-primary font-bold rounded-xl text-xs hover:bg-blue-50 transition shadow-sm"
            >
              + Quick Collect Fee
            </Link>
          </div>
        </div>

        {/* Recent Collections Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-bold text-slate-800 text-sm">Recent Fee Collection Transactions</h3>
            <Link to="/fees/collect" className="text-xs font-semibold text-primary hover:underline">
              View All Transactions →
            </Link>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-6 py-3.5">Receipt #</th>
                  <th className="px-6 py-3.5">Student Name</th>
                  <th className="px-6 py-3.5">Class</th>
                  <th className="px-6 py-3.5">Payment Method</th>
                  <th className="px-6 py-3.5">Date</th>
                  <th className="px-6 py-3.5 text-right">Paid Amount</th>
                  <th className="px-6 py-3.5 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm font-medium text-slate-700">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-slate-400">
                      <div className="flex flex-col items-center gap-2">
                        <svg className="w-8 h-8 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Loading recent transactions...</span>
                      </div>
                    </td>
                  </tr>
                ) : !stats?.recent_payments || stats.recent_payments.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-slate-500">
                      No fee payments recorded yet. Click "Collect Fee" to record your first payment.
                    </td>
                  </tr>
                ) : (
                  stats.recent_payments.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50/80 transition">
                      <td className="px-6 py-4 font-mono font-bold text-slate-800 text-xs">
                        {item.receipt_no}
                      </td>
                      <td className="px-6 py-4 font-bold text-slate-900">
                        {item.student_name}
                      </td>
                      <td className="px-6 py-4 font-semibold text-slate-700">
                        Class {item.class_id}
                      </td>
                      <td className="px-6 py-4 capitalize text-xs text-slate-600">
                        {item.payment_method}
                      </td>
                      <td className="px-6 py-4 text-xs text-slate-500">
                        {new Date(item.payment_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right font-mono font-extrabold text-emerald-600">
                        Rs. {parseFloat(item.paid_amount).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => setSelectedReceipt(item)}
                          className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold text-xs rounded-xl border border-blue-200 transition inline-flex items-center gap-1.5"
                        >
                          <span>🖨️</span>
                          <span>Print Receipt</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </main>

      {/* Historical Fee Receipt Reprint Modal */}
      {selectedReceipt && (
        <FeeReceipt
          receiptData={selectedReceipt}
          onClose={() => setSelectedReceipt(null)}
        />
      )}

    </div>
  );
};

export default FeeDashboard;
